import SirBackgroundImage from "../../assets/sir-banner.jpg";
import SirBackgroundImageMobile from "../../assets/sir-banner-mobile.jpg";

// ============================================================================
// SIR 2026 (Special Intensive Revision) — search scope & display configuration
//
// A TEMPORARY, limited-time search engine that sits alongside the campaign app.
// Intentionally self-contained: no selectedPanel, no permissions, none of the
// panel plumbing.
//
// Backed by the election API's SIRVoterList / SIRPart tables, imported from the
// ECI "View Enumeration Form Details" PDFs by tools/import-sir.js.
// See docs/sir-search.md for the full contract.
//
// 👉 Everything you are likely to want to change lives in this file.
// ============================================================================

/** Assembly Constituency the SIR list covers. Sent as `constno`. */
export const SIR_ASSEMBLY_NO = 141;
export const SIR_ASSEMBLY_NAME = "141 - Ulhasnagar";

/**
 * Backend API names.
 * ⚠️ These are registered ONLY under /api/ (not /apisecure/), so the page calls
 * them with `{ open: true }`, which makes the API client use the /api/ base.
 */
export const SIR_API = {
  search: "SearchSIR",
  parts: "GetSIRPartsByConstno",
  reasonCounts: "GetSIRReasonCountByConstno",

  /**
   * ⚠️ REMOVED ON PURPOSE — the old campaign roll (SearchMixData) is a
   * DIFFERENT survey with its own part / serial numbering, spellings and ages.
   * Mixing it into a SIR result contradicted the published 2026 PDF, so the
   * voter detail panel and the WhatsApp slip now read SIR data only. Do not
   * reintroduce a `roll` route here.
   */

  /**
   * The SIR 2026 DRAFT ROLL — who is actually ON the new roll, OCR'd from the
   * published draft PDFs into SIRDraftRoll / SIRDraftPart.
   *
   * This answers a different question from `search` above. The enumeration list
   * says why a voter was FLAGGED; the draft roll says whether they survived,
   * and gives their NEW part / voter number / polling station. Being flagged did
   * not guarantee deletion, and not being flagged did not guarantee inclusion —
   * only the draft roll settles it.
   */
  draftSearch: "SearchSIRDraft",
  draftParts: "GetSIRDraftPartsByConstno",
  combined: "GetSIRCombinedByCardNo",

  /**
   * Mobile numbers collected on this screen, so voter details can be shared on
   * WhatsApp. Backed by SIRVoterMobile (sql/005_sir_voter_mobile.sql).
   *   mobileUsage    — which voters already hold a number (drives the cap)
   *   voterMobile    — the current number saved against one EPIC
   *   saveMobile     — save; the server REFUSES a 3rd voter on the same number
   *   allMobiles     — the super-admin register
   *   removeMobile   — super admin retires an entry (frees the number's slot)
   */
  mobileUsage: "GetSIRMobileUsage",
  voterMobile: "GetSIRVoterMobileByCardNo",
  saveMobile: "AddSIRVoterMobile",
  allMobiles: "GetAllSIRVoterMobiles",
  removeMobile: "RemoveSIRVoterMobile",

  /**
   * Super-admin only. `users` is the roster of OTP-verified mobile numbers with
   * their usage and credit balance; `addCredits` grants more. BOTH re-read
   * Users.SuperAdmin server-side for the calling UserId — the browser's
   * SuperAdmin flag only decides whether the button is drawn.
   */
  users: "GetSirUsers",
  /** Every credit grant ever made — the ledger behind the balances. */
  creditGrants: "GetSirCreditGrants",
  addCredits: "AddSirCredits",
};

/**
 * How many DIFFERENT voters may share one mobile number. Two lets a couple or
 * two family members share a phone; the third is refused. The server enforces
 * the same number in AddSIRVoterMobile — this constant only drives the UI copy.
 */
export const SIR_MOBILE_MAX_VOTERS = 2;

/** Mobile numbers are exactly this many digits, no country code. */
export const SIR_MOBILE_LENGTH = 10;

/** Signature line on every shared message. */
export const SIR_SHARE_FOOTER = "By SmartSearch - 9767728530";

/** Who the voter should thank / call. Last line of every shared message. */
export const SIR_SERVICE_FOOTER =
  "Service By - Shri Kumar Ailani (MLA - Ulhasnagar 141) - 9890000005";

/**
 * The published PDF wraps the venue in boilerplate:
 *   "Type of Polling Station General (Male/Female/General) 112 - Gol Maidan,
 *    Ulhasnagar Camp No. 1,P.N.421001 Lat: 19.22545 lon: 73.16633. Number of
 *    Auxiliary Polling Stations in this part :"
 * None of that helps a voter find the booth, and it is most of the message's
 * length — so it is trimmed down to the place itself.
 */
export function sirShortStation(text) {
  let out = String(text || "").trim();
  if (!out) return "";
  out = out.replace(/^\s*Type of Polling Station[^0-9]*/i, "");
  out = out.replace(/\s*Lat:\s*[-\d.]+\s*lon:\s*[-\d.]+\.?/i, "");
  out = out.replace(/\s*Number of Auxiliary Polling Stations[^]*$/i, "");
  return out.replace(/\s{2,}/g, " ").replace(/[\s,;:.-]+$/, "").trim();
}

/**
 * The WhatsApp message. Plain text on purpose — it travels through a normal
 * wa.me / web.whatsapp.com link, so there is no per-message cost and nothing
 * Meta can switch off. An image could not travel this way.
 *
 * Lines with no value are dropped rather than printed as "-".
 *
 * ⚠️ SIR DATA ONLY — do not reintroduce the old campaign roll here.
 * An earlier version enriched this message from `SearchMixData` (the pre-revision
 * datafile_141 roll). That is a DIFFERENT survey, and mixing it produced a slip
 * that contradicted the published PDF the voter is being told about:
 *
 *   - the name came back rearranged surname-first and respelled
 *     ("Ailani Kumar Uttamchand" where the draft roll reads
 *      "Kumar Uttamchand Aylani")
 *   - the age was the old roll's (66) rather than the draft roll's (68)
 *   - a Marathi name and a street address were added that appear nowhere in
 *     the SIR record at all
 *
 * Every field below therefore comes from the draft roll (`combined.Draft*`,
 * OCR'd from the published 2026 PDF) and falls back only to the SIR enumeration
 * row — never to the old roll.
 */
export function sirShareMessage({ row, combined, verdict }) {
  const clean = (v) =>
    v != null && String(v).trim() !== "" && String(v).trim() !== "-"
      ? String(v).trim()
      : "";

  // Prefer the draft roll: it is the document the voter can actually be shown.
  const name = clean(combined?.DraftName || row?.ElectorName);
  const relativeName = clean(combined?.DraftRelative || row?.RelativeName);
  const relation = clean(combined?.DraftRelation || row?.Relation);
  const relative = [relativeName, relation && `(${relation})`]
    .filter(Boolean)
    .join(" ");
  const age = clean(combined?.DraftAge != null ? combined.DraftAge : row?.age);
  // "65 / Female" -> "65/F": the same fact in a third of the width.
  const sexShort = clean(combined?.DraftSex || row?.sex).charAt(0).toUpperCase();
  const ageSex = [age, sexShort].filter(Boolean).join("/");

  const onDraft = combined && combined.DraftVno != null;
  const draftLoc = onDraft
    ? `भाग ${combined.DraftPart} · अ.क्र. ${combined.DraftVno}`
    : "";

  // "Removed from the roll — Death" reads better than the bare badge label.
  const reason = clean(combined ? combined.SirReason : row?.Reason);
  const hasReason = reason && reason !== "(none)";
  const verdictLine = [verdict?.label, hasReason ? reason : null]
    .filter(Boolean)
    .join(" — ");

  // The venue proper, with the PDF boilerplate stripped. StationAddress is the
  // building; PollingStation is the area — the building alone is enough when
  // both are present.
  const station =
    sirShortStation(combined?.StationAddress) ||
    sirShortStation(combined?.PollingStation);

  return [
    "🗳️ *SIR 2026 — मतदार माहिती*",
    "",
    name ? `👤 *${name}*${ageSex ? ` · ${ageSex}` : ""}` : null,
    relative ? `👨‍👦 ${relative}` : null,
    row?.CardNo ? `🆔 ${clean(row.CardNo)}` : null,
    verdictLine
      ? `${SIR_VERDICT_EMOJI[verdict?.key] || "ℹ️"} *${verdictLine}*`
      : null,
    draftLoc ? `📋 ${draftLoc}` : null,
    station ? `📍 ${station}` : null,
    "",
    SIR_SHARE_FOOTER,
    SIR_SERVICE_FOOTER,
  ]
    .filter((v) => v !== null && v !== undefined)
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * Which dataset the search box queries.
 *   flagged — SIRVoterList: voters the enumeration flagged, and why
 *   draft   — SIRDraftRoll: who is on the published 2026 roll, and where they vote
 */
export const SIR_SOURCES = [
  { value: "flagged", label: "Flagged list (enumeration)", api: SIR_API.search },
  { value: "draft", label: "Draft roll 2026", api: SIR_API.draftSearch },
];

/**
 * Dataset the page opens on. The draft roll is the one that answers the question
 * voters actually ask — "am I on the 2026 roll, and where do I vote?" — so it
 * leads. Must be one of the `value`s above.
 */
export const SIR_DEFAULT_SOURCE = "draft";

/** Server caps this at 1000; 200 is its default. */
export const SIR_SEARCH_LIMIT = 500;

/** Rows rendered per page (client-side paging). */
export const SIR_PAGE_SIZE = 25;

/**
 * Deletion reasons. These are the complete confirmed vocabulary produced by the
 * importer's REASON_MAP — a blank cell in the PDF means the voter was enumerated
 * with no deletion reason, which is stored as NULL.
 */
export const SIR_REASONS = [
  { value: "", label: "All reasons", color: "#6c757d" },
  { value: "Untraceable/Absent", label: "Untraceable / Absent", color: "#fd7e14" },
  { value: "Permanently Shifted", label: "Permanently Shifted", color: "#6f42c1" },
  { value: "Death", label: "Death", color: "#dc3545" },
  { value: "Already enrolled", label: "Already enrolled", color: "#0d6efd" },
  { value: "EF Refused", label: "EF Refused", color: "#b8860b" },
];

/** Field carrying the deletion reason. Rendered as a coloured badge. */
export const SIR_REASON_FIELD = "Reason";

/** Shown when Reason is NULL (voter enumerated, not flagged for deletion). */
export const SIR_NO_REASON_LABEL = "Enumerated";
export const SIR_NO_REASON_COLOR = "#198754";

/**
 * Keys returned by the API but not shown as columns.
 * `name_english`, `esurname` and `relative_english` are derived splits of
 * ElectorName / RelativeName that exist so the server can index and search them
 * — they carry no extra information on screen. Delete an entry here to show it.
 */
export const SIR_HIDDEN_COLUMNS = new Set([
  "SIRId",
  "constno",
  "name_english",
  "esurname",
  "relative_english",
]);

/**
 * Preferred column order. Any key present in the data but missing here is
 * appended after these, so a schema change still renders.
 */
export const SIR_COLUMN_ORDER = [
  "yadibhag",
  "vno",
  "CardNo",
  "ElectorName",
  "RelativeName",
  "Relation",
  "age",
  "sex",
  SIR_REASON_FIELD,
  "RefEpic",
];

/** Header labels. Unknown keys fall back to the raw key name. */
export const SIR_COLUMN_LABELS = {
  yadibhag: "Part",
  vno: "Sr. No",
  CardNo: "EPIC / Voter ID",
  ElectorName: "Elector Name",
  name_english: "Name",
  esurname: "Surname",
  RelativeName: "Relative Name",
  relative_english: "Relative",
  Relation: "Relation",
  age: "Age",
  sex: "Sex",
  Reason: "Reason",
  RefEpic: "Ref. EPIC",
};

/** Column widths (CSS values). Unknown keys fall back to "auto". */
export const SIR_COLUMN_WIDTHS = {
  yadibhag: "5%",
  vno: "6%",
  CardNo: "12%",
  ElectorName: "21%",
  RelativeName: "19%",
  Relation: "8%",
  age: "5%",
  sex: "5%",
  Reason: "12%",
  RefEpic: "11%",
};

/** Distinct look so operators can never confuse SIR mode with the campaign app. */
export const SIR_THEME = {
  bar: "#1565c0",
  barText: "#ffffff",
  accent: "#0d47a1",
  page: "#eef2f7",
};

/**
 * Fixed party banner shown at the very top of the SIR search screen.
 * All app content sits below it, on the plain white area.
 * The image is bundled from src/assets and is NOT configurable — no env var,
 * no server value, no user setting may replace it.
 */
export const SIR_BACKGROUND_STYLE = Object.freeze({
  width: "100%",
  flexShrink: 0,
  /* Banner keeps its own 6145:903 aspect ratio, so nothing is ever cropped. */
  aspectRatio: "6145 / 903",
  backgroundColor: SIR_THEME.page,
  backgroundImage: `url(${SirBackgroundImage})`,
  backgroundSize: "100% 100%",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  borderBottom: `3px solid ${SIR_THEME.accent}`,
});

/**
 * Narrow-screen / APK banner. The wide strip is 6.8:1, which collapses to a
 * ~55px sliver on a phone, so portrait uses its own artwork.
 * Replace src/assets/sir-banner-mobile.jpg (830 x 244) to change it. The
 * artwork is the banner strip cropped off the top of
 * H:\Mix_Data\corel\jpg\SmartSearch Sir Main Page Mobile Screen.jpg
 * (its top 244 rows), not the whole phone mockup. Shipped at the mockup's
 * native width - it was exported at only 830px, and upscaling adds blur, not
 * detail. A sharper banner needs a larger export from Corel, not a change here.
 *
 * The ratio here MUST match the artwork. backgroundSize is "100% 100%", which
 * stretches rather than crops, so a mismatch silently distorts the faces on
 * the banner instead of failing visibly.
 */
export const SIR_BACKGROUND_STYLE_MOBILE = Object.freeze({
  ...SIR_BACKGROUND_STYLE,
  aspectRatio: "830 / 244",
  backgroundImage: `url(${SirBackgroundImageMobile})`,
});

/** Width at or below which the portrait banner is used. */
export const SIR_BANNER_MOBILE_MAX_WIDTH = 700;

/** Colour for a Reason value (null/blank → the "enumerated" colour). */
export function reasonMeta(value) {
  if (value == null || String(value).trim() === "" || value === "(none)") {
    return { label: SIR_NO_REASON_LABEL, color: SIR_NO_REASON_COLOR };
  }
  const key = String(value).trim().toLowerCase();
  const hit = SIR_REASONS.find(
    (r) => r.value && r.value.toLowerCase() === key
  );
  return hit
    ? { label: hit.label, color: hit.color }
    : { label: String(value), color: "#6c757d" };
}

/**
 * The combined verdict for one voter, from the two SIR datasets.
 *
 *   on the draft roll + no flag    -> Retained
 *   on the draft roll + flagged    -> Flagged but retained (objection resolved)
 *   NOT on the draft roll + flagged-> Deleted, with the recorded reason
 *   NOT on the draft roll, no flag -> Missing with no reason on record  <-- most actionable
 */
export const SIR_VERDICT = {
  RETAINED: { key: "RETAINED", label: "On the 2026 roll", color: "#198754" },
  FLAGGED_RETAINED: { key: "FLAGGED_RETAINED", label: "Flagged but retained", color: "#0d6efd" },
  DELETED: { key: "DELETED", label: "Removed from the roll", color: "#dc3545" },
  MISSING: { key: "MISSING", label: "Missing — no reason recorded", color: "#fd7e14" },
  UNKNOWN: { key: "UNKNOWN", label: "Not determined", color: "#6c757d" },
};

/** Emoji used for each verdict in the shared WhatsApp message. */
export const SIR_VERDICT_EMOJI = {
  RETAINED: "✅",
  FLAGGED_RETAINED: "✅",
  DELETED: "❌",
  MISSING: "⚠️",
  UNKNOWN: "ℹ️",
};

export function sirVerdict({ onDraft, reason }) {
  const flagged = !!(reason && String(reason).trim() && reason !== "(none)");
  if (onDraft === true) return flagged ? SIR_VERDICT.FLAGGED_RETAINED : SIR_VERDICT.RETAINED;
  if (onDraft === false) return flagged ? SIR_VERDICT.DELETED : SIR_VERDICT.MISSING;
  return SIR_VERDICT.UNKNOWN;
}

/**
 * Turn whatever the operator typed into structured search fields.
 *
 * Recognised, in order:
 *   NMW4120184        -> exact EPIC
 *   141/12/332        -> assembly / part / voter no   (also 141-12-332, 141 12 332)
 *   12/332            -> part / voter no
 *   anything else     -> free text
 *
 * Word ORDER does not matter for names: the server tests every word against
 * surname, given name, relative and full name with OR, so "sanjay ailani" and
 * "ailani sanjay" both match the same person. Nothing to do here for that.
 */
export function parseSirQuery(raw) {
  const t = String(raw ?? "").trim();
  if (!t) return {};

  if (/^[A-Za-z]{3}\d{7}$/.test(t)) {
    return { CardNo: t.toUpperCase(), kind: "epic" };
  }

  // assembly / part / voter
  let m = t.match(/^(\d{1,3})\s*[/\-\s]\s*(\d{1,3})\s*[/\-\s]\s*(\d{1,5})$/);
  if (m) {
    return {
      constno: Number(m[1]),
      yadibhag: Number(m[2]),
      vno: Number(m[3]),
      kind: "acPartVno",
    };
  }

  // part / voter
  m = t.match(/^(\d{1,3})\s*[/\-]\s*(\d{1,5})$/);
  if (m) {
    return { yadibhag: Number(m[1]), vno: Number(m[2]), kind: "partVno" };
  }

  return { search: t, kind: "text" };
}

/** One-line explanation of how a query was read, shown under the search box. */
export function describeSirQuery(p) {
  switch (p.kind) {
    case "epic":
      return `EPIC ${p.CardNo}`;
    case "acPartVno":
      return `AC ${p.constno} · part ${p.yadibhag} · voter ${p.vno}`;
    case "partVno":
      return `part ${p.yadibhag} · voter ${p.vno}`;
    default:
      return "";
  }
}
